import { Box } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ManualChapterSection,
  type ManualChapterText,
  type ManualSlots,
  type ManualTopicText,
} from "./ManualChapterSection";
import type { ManualChapterId } from "./manualChapters";
import { jumpToManualSection } from "./manualJump";
import { MANUAL_STYLES } from "./manualStyles";
import { ManualTocBar } from "./ManualTocBar";
import { MANUAL_CHAPTERS } from "./manualTopics";

/** 절 목록의 지금 절 — 화면 위에서 25%~35% 띠에 걸린 절 */
const CURRENT_TOPIC_MARGIN = "-25% 0px -65% 0px";

/**
 * 로그인한 사용자의 매뉴얼 본문: 여덟 장 전부(장마다 왼쪽 라벨 + 절) + 절 목록의 지금 절 표시 + 넓은 화면 「전체 목차」 카드
 * 열고 닫기 + 좁은 화면 아래 막대.
 */
export function ManualMemberBody({
  chapters,
  topics,
  mappingGuideEntry,
}: ManualSlots & {
  chapters: Readonly<Record<ManualChapterId, ManualChapterText>>;
  topics: Readonly<Record<string, ManualTopicText>>;
}) {
  const docRef = useRef<HTMLDivElement>(null);
  /** 지금 읽는 절 — 스크롤마다 바뀌므로 상태가 아니라 ref(좁은 화면 목차가 펼칠 때 읽는다) */
  const currentTopicRef = useRef<string | null>(null);
  const [allChaptersOpen, setAllChaptersOpen] = useState(false);
  /** 마지막으로 누른 「전체 목차」 단추(장마다 하나씩) — Esc 로 닫으면 초점을 여기로 돌린다 */
  const allChaptersToggleRef = useRef<HTMLButtonElement | null>(null);

  // 장·절 주소(소식의 「시연 영상 보기」·로그인 뒤 돌아옴·직접 연 주소)로 왔으면 칠하기 전에 그 자리로 간다 — 라우터·브라우저의
  // 해시 이동은 로그인 확인 중(본문 없음)에 끝나 첫 화면에 머문다. `?step=N` 은 그 절 플레이어가 주소에서 읽는다.
  useLayoutEffect(() => {
    const id = window.location.hash.slice(1);
    const target = id ? document.getElementById(id) : null;
    if (target && docRef.current?.contains(target)) jumpToManualSection(id);
  }, []);

  // 절 목록에 지금 읽는 절을 표시한다 — 스크롤마다 바뀌므로 DOM 속성으로만
  useEffect(() => {
    const doc = docRef.current;
    if (!doc || typeof IntersectionObserver === "undefined") return undefined;
    const links = new Map(
      [...doc.querySelectorAll<HTMLAnchorElement>("[data-toc-for]")].map(
        (link) => [link.dataset.tocFor, link]
      )
    );
    let current: string | null = null;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.target.id === current) return;
          current = entry.target.id;
          currentTopicRef.current = current;
          links.forEach((link) => link.removeAttribute("aria-current"));
          links.get(current)?.setAttribute("aria-current", "true");
        }),
      { rootMargin: CURRENT_TOPIC_MARGIN }
    );
    doc
      .querySelectorAll("article[id]")
      .forEach((topic) => observer.observe(topic));
    return () => observer.disconnect();
  }, []);

  // 넓은 화면 전체 목차 카드: Esc(초점은 누른 단추로)나 카드·단추 줄 밖 누름으로 닫는다 — 좁은 화면 막대와 같은 닫기
  useEffect(() => {
    if (!allChaptersOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setAllChaptersOpen(false);
      allChaptersToggleRef.current?.focus();
    };
    const handleClick = (event: MouseEvent) => {
      if (!(event.target as Element).closest("[data-all-chapters]"))
        setAllChaptersOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClick);
    };
  }, [allChaptersOpen]);

  const toggleAllChapters = (toggle: HTMLButtonElement) => {
    allChaptersToggleRef.current = toggle;
    setAllChaptersOpen((isOpen) => !isOpen);
  };
  const jumpToChapter = (id: string) => {
    setAllChaptersOpen(false);
    jumpToManualSection(id);
  };

  return (
    <>
      <Box ref={docRef} sx={MANUAL_STYLES.doc}>
        {MANUAL_CHAPTERS.map((chapter, index) => (
          <ManualChapterSection
            key={chapter.id}
            chapter={chapter}
            index={index}
            chapters={chapters}
            topics={topics}
            slots={{ mappingGuideEntry }}
            allChaptersOpen={allChaptersOpen}
            onAllChaptersToggle={toggleAllChapters}
            onChapterJump={jumpToChapter}
          />
        ))}
      </Box>
      <ManualTocBar
        chapters={chapters}
        topics={topics}
        currentTopicRef={currentTopicRef}
      />
    </>
  );
}
